(function () {
	const ELEMENT_NAME = "delivery-estimate";
	const CACHE_PREFIX = "deliveryEstimate:v1:";
	const WEEKDAYS = {
		sun: 0,
		mon: 1,
		tue: 2,
		wed: 3,
		thu: 4,
		fri: 5,
		sat: 6,
	};

	const parseNumber = (value, fallback) => {
		const number = Number.parseInt(value, 10);
		return Number.isFinite(number) && number >= 0 ? number : fallback;
	};

	const parseDays = (value, fallback) => {
		const days = String(value || "")
			.split(",")
			.map((day) => day.trim().slice(0, 3).toLowerCase())
			.filter((day) => day in WEEKDAYS)
			.map((day) => WEEKDAYS[day]);

		return days.length ? new Set(days) : new Set(fallback);
	};

	const parseCutoff = (value) => {
		const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
		if (!match) return { hour: 14, minute: 0 };

		const hour = Math.min(Math.max(Number.parseInt(match[1], 10), 0), 23);
		const minute = Math.min(Math.max(Number.parseInt(match[2], 10), 0), 59);
		return { hour, minute };
	};

	const normalizeTimeZone = (timeZone) => {
		try {
			new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
			return timeZone;
		} catch (error) {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		}
	};

	const getZonedParts = (date, timeZone) => {
		const formatter = new Intl.DateTimeFormat("en-US", {
			timeZone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hourCycle: "h23",
		});
		const parts = Object.fromEntries(
			formatter.formatToParts(date).map((part) => [part.type, part.value])
		);

		return {
			year: Number.parseInt(parts.year, 10),
			month: Number.parseInt(parts.month, 10),
			day: Number.parseInt(parts.day, 10),
			hour: Number.parseInt(parts.hour, 10),
			minute: Number.parseInt(parts.minute, 10),
			second: Number.parseInt(parts.second, 10),
		};
	};

	const toUtcDate = (ymd) => {
		return new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
	};

	const toYmd = (date) => {
		return {
			year: date.getUTCFullYear(),
			month: date.getUTCMonth() + 1,
			day: date.getUTCDate(),
		};
	};

	const addCalendarDays = (ymd, days) => {
		const date = toUtcDate(ymd);
		date.setUTCDate(date.getUTCDate() + days);
		return toYmd(date);
	};

	const getWeekday = (ymd) => toUtcDate(ymd).getUTCDay();

	const addEligibleDays = (startDate, count, allowedDays) => {
		if (count <= 0) return startDate;

		let date = startDate;
		let added = 0;
		let safety = 0;

		while (added < count && safety < 400) {
			date = addCalendarDays(date, 1);
			if (allowedDays.has(getWeekday(date))) {
				added += 1;
			}
			safety += 1;
		}

		return date;
	};

	const formatDate = (ymd, format) => {
		const date = toUtcDate(ymd);

		if (format === "DD MMM") {
			return new Intl.DateTimeFormat("en-US", {
				timeZone: "UTC",
				day: "2-digit",
				month: "short",
			}).format(date);
		}

		if (format === "MM/DD") {
			return new Intl.DateTimeFormat("en-US", {
				timeZone: "UTC",
				month: "2-digit",
				day: "2-digit",
			}).format(date);
		}

		return new Intl.DateTimeFormat("en-US", {
			timeZone: "UTC",
			month: "short",
			day: format === "MMM D" ? "numeric" : "2-digit",
		}).format(date);
	};

	const renderTemplate = (template, values) => {
		return String(template || "Estimated delivery: {delivery_min}-{delivery_max}")
			.replaceAll("{delivery_min}", values.deliveryMin)
			.replaceAll("{delivery_max}", values.deliveryMax)
			.replaceAll("{ready_min}", values.readyMin)
			.replaceAll("{ready_max}", values.readyMax)
			.replaceAll("{order_date}", values.orderDate);
	};

	const hashString = (value) => {
		let hash = 5381;
		for (let i = 0; i < value.length; i += 1) {
			hash = (hash * 33) ^ value.charCodeAt(i);
		}
		return (hash >>> 0).toString(36);
	};

	const getCache = (key) => {
		try {
			const cached = window.localStorage.getItem(key);
			if (!cached) return null;

			const parsed = JSON.parse(cached);
			if (!parsed || Date.now() >= parsed.expiresAt) {
				window.localStorage.removeItem(key);
				return null;
			}

			return parsed.value;
		} catch (error) {
			return null;
		}
	};

	const setCache = (key, value, expiresAt) => {
		try {
			window.localStorage.setItem(key, JSON.stringify({ value, expiresAt }));
		} catch (error) {
			// Storage can be unavailable in private browsing or constrained webviews.
		}
	};

	const getExpiresAt = (parts, config) => {
		const currentMinutes = parts.hour * 60 + parts.minute;
		const cutoffMinutes = config.cutoff.hour * 60 + config.cutoff.minute;
		let ttlMinutes;

		if (config.useCutoff && currentMinutes < cutoffMinutes) {
			ttlMinutes = cutoffMinutes - currentMinutes;
		} else {
			ttlMinutes = 24 * 60 - currentMinutes;
		}

		const ttlMs = Math.min(Math.max(ttlMinutes * 60 - parts.second, 60) * 1000, 24 * 60 * 60 * 1000);
		return Date.now() + ttlMs;
	};

	class DeliveryEstimate extends HTMLElement {
		connectedCallback() {
			if (this.dataset.deliveryEstimateInitialized === "true") return;
			this.dataset.deliveryEstimateInitialized = "true";

			const config = this.getConfig();
			const cacheKey = this.getCacheKey(config);
			const cached = getCache(cacheKey);

			if (cached) {
				this.render(cached);
				return;
			}

			const result = this.calculate(config);
			setCache(cacheKey, result, result.expiresAt);
			this.render(result);
		}

		getConfig() {
			const processingMin = parseNumber(this.dataset.processingMin, 2);
			const processingMax = parseNumber(this.dataset.processingMax, 4);
			const transitMin = parseNumber(this.dataset.transitMin, 4);
			const transitMax = parseNumber(this.dataset.transitMax, 6);

			return {
				messageTemplate: this.dataset.messageTemplate,
				originText: this.dataset.originText || "",
				showOrigin: this.dataset.showOrigin === "true",
				processingMin: Math.min(processingMin, processingMax),
				processingMax: Math.max(processingMin, processingMax),
				transitMin: Math.min(transitMin, transitMax),
				transitMax: Math.max(transitMin, transitMax),
				processingDays: parseDays(this.dataset.processingDays, [1, 2, 3, 4, 5]),
				transitDays: parseDays(this.dataset.transitDays, [0, 1, 2, 3, 4, 5, 6]),
				useCutoff: this.dataset.useCutoff === "true",
				cutoff: parseCutoff(this.dataset.cutoffTime),
				timeZone: normalizeTimeZone(this.dataset.timeZone || "Europe/Rome"),
				dateFormat: this.dataset.dateFormat || "MMM DD",
			};
		}

		getCacheKey(config) {
			const serializableConfig = {
				messageTemplate: config.messageTemplate,
				originText: config.originText,
				showOrigin: config.showOrigin,
				processingMin: config.processingMin,
				processingMax: config.processingMax,
				transitMin: config.transitMin,
				transitMax: config.transitMax,
				processingDays: Array.from(config.processingDays),
				transitDays: Array.from(config.transitDays),
				useCutoff: config.useCutoff,
				cutoff: config.cutoff,
				timeZone: config.timeZone,
				dateFormat: config.dateFormat,
			};

			return CACHE_PREFIX + hashString(JSON.stringify(serializableConfig));
		}

		calculate(config) {
			const now = new Date();
			const parts = getZonedParts(now, config.timeZone);
			let orderDate = {
				year: parts.year,
				month: parts.month,
				day: parts.day,
			};

			const currentMinutes = parts.hour * 60 + parts.minute;
			const cutoffMinutes = config.cutoff.hour * 60 + config.cutoff.minute;
			if (config.useCutoff && currentMinutes >= cutoffMinutes) {
				orderDate = addCalendarDays(orderDate, 1);
			}

			const readyMin = addEligibleDays(
				orderDate,
				config.processingMin,
				config.processingDays
			);
			const readyMax = addEligibleDays(
				orderDate,
				config.processingMax,
				config.processingDays
			);
			const deliveryMin = addEligibleDays(
				readyMin,
				config.transitMin,
				config.transitDays
			);
			const deliveryMax = addEligibleDays(
				readyMax,
				config.transitMax,
				config.transitDays
			);
			const values = {
				orderDate: formatDate(orderDate, config.dateFormat),
				readyMin: formatDate(readyMin, config.dateFormat),
				readyMax: formatDate(readyMax, config.dateFormat),
				deliveryMin: formatDate(deliveryMin, config.dateFormat),
				deliveryMax: formatDate(deliveryMax, config.dateFormat),
			};

			return {
				message: renderTemplate(config.messageTemplate, values),
				origin: config.showOrigin ? config.originText : "",
				expiresAt: getExpiresAt(parts, config),
			};
		}

		render(result) {
			const message = this.querySelector("[data-delivery-estimate-message]");
			const origin = this.querySelector("[data-delivery-estimate-origin]");

			if (message) message.textContent = result.message;
			if (origin) {
				origin.textContent = result.origin;
				origin.hidden = !result.origin;
			}
		}
	}

	if (!customElements.get(ELEMENT_NAME)) {
		customElements.define(ELEMENT_NAME, DeliveryEstimate);
	}
})();
